import { cn } from "@aircall/ds";
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
} from "react";

export interface SectionProps extends ComponentPropsWithoutRef<"div"> {
  /**
   * Test hook forwarded to the wrapper element. Declared explicitly (rather
   * than relying on the `data-*` index signature) so it resolves to `string`
   * — matching the legacy `SectionLayoutProps`. Defaults to `"section-layout"`.
   */
  "data-test"?: string;
}

export interface SectionHeaderProps extends ComponentPropsWithoutRef<"div"> {
  /**
   * Trailing addon rendered after the title content, e.g. a <BetaTag/> badge.
   * Replaces the old `beta?: boolean` prop, decoupling the block from the
   * i18n-bound BetaTag in `src/components`. Rendered as `{children}{addon}` so
   * it trails the title, preserving the legacy `<p/>` then `{beta && <BetaTag/>}`
   * order.
   */
  addon?: ReactNode;
}

export interface SectionTitleProps extends ComponentPropsWithoutRef<"p"> {}

/**
 * Compound root / wrapper. Replaces the legacy `SectionLayout`: a vertical flex
 * stack that groups a header row and the section body. Usable on its own
 * (Csat/Sentiment render a custom header inside it, no SectionHeader/SectionTitle).
 *
 * ```tsx
 * <Section data-test="number-section">
 *   <SectionHeader>
 *     <SectionTitle>{t("number")}</SectionTitle>
 *   </SectionHeader>
 *   {body}
 * </Section>
 * ```
 */
export const Section = forwardRef<HTMLDivElement, Section.Props>(
  (componentProps: Section.Props, forwardRef: ForwardedRef<HTMLDivElement>) => {
    const {
      className,
      children,
      "data-test": dataTest = "section-layout",
      ...props
    } = componentProps;

    return (
      <div
        ref={forwardRef}
        data-slot="section"
        data-test={dataTest}
        className={cn("flex flex-col gap-3", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
Section.displayName = "Section";

export namespace Section {
  export type Props = SectionProps;
}

/**
 * Header ROW container. Replaces the legacy `SectionHeader`'s outer div. Lays
 * out the title (via children, normally a `SectionTitle`) and an optional
 * trailing addon slot side by side, wrapping on narrow viewports. Has NO i18n
 * and NO BetaTag coupling: consumers that want a beta tag inject `<BetaTag/>`
 * via the `addon` prop. Optional — Csat/Sentiment skip it entirely.
 */
export const SectionHeader = forwardRef<HTMLDivElement, SectionHeader.Props>(
  (
    componentProps: SectionHeader.Props,
    forwardRef: ForwardedRef<HTMLDivElement>
  ) => {
    const { className, children, addon, ...props } = componentProps;

    return (
      <div
        ref={forwardRef}
        data-slot="section-header"
        className={cn(
          "flex flex-wrap items-center gap-2 xl:flex-nowrap",
          className
        )}
        {...props}
      >
        {children}
        {addon}
      </div>
    );
  }
);
SectionHeader.displayName = "SectionHeader";

export namespace SectionHeader {
  export type Props = SectionHeaderProps;
}

/**
 * The section title leaf. Replaces the legacy `SectionHeader`'s inner `<p>`.
 * Renders its children as the bold foreground title text, mirroring the `Line`
 * block's `LineTitle` precedent (every meaningful node in a compound is its own
 * data-slot part). Placed inside `SectionHeader`.
 */
export const SectionTitle = forwardRef<
  HTMLParagraphElement,
  SectionTitle.Props
>(
  (
    componentProps: SectionTitle.Props,
    forwardRef: ForwardedRef<HTMLParagraphElement>
  ) => {
    const { className, children, ...props } = componentProps;

    return (
      <p
        ref={forwardRef}
        data-slot="section-title"
        className={cn("text-sm font-semibold text-foreground", className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);
SectionTitle.displayName = "SectionTitle";

export namespace SectionTitle {
  export type Props = SectionTitleProps;
}
